import React, { useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { Spin } from 'antd';
import LearningSection from 'LearningManager/components/LearningSection';
import styles from 'LearningManager/styles/QuickSetup.module.scss';
import { useStores } from 'Hooks/useStore';
import useAssignmentTenantId from 'LearningManager/hooks/useAssignmentTenantId';
import { Status } from 'app-types';
import course1 from 'LearningManager/images/course-1.png';
import course2 from 'LearningManager/images/course-2.png';
import course3 from 'LearningManager/images/course-3.jpg';
import course4 from 'LearningManager/images/course-4.jpg';

const PLACEHOLDER_IMAGES = [course1, course2, course3, course4];

const slugify = (value: string) =>
	value
		.toLowerCase()
		.replace(/\s+/g, '-')
		.replace(/[^a-z0-9-]/g, '');

const LearningPath: React.FC = observer(() => {
	const { learningManagerStore } = useStores();
	const { learningPaths, coursesById, status } = learningManagerStore;
	const tenantId = useAssignmentTenantId();

	useEffect(() => {
		learningManagerStore.listLearningPaths({
			offset: 0,
			setup_type: 'quick',
			...(tenantId ? { tenant_id: tenantId } : {})
		});
		// Single bulk load of the activity catalog into coursesById — replaces
		// the previous one-request-per-course fetch.
		learningManagerStore.fetchAllCourses(100, tenantId);
	}, [tenantId]);

	const isLoading =
		status.listLearningPaths === Status.Pending ||
		status.listActivities === Status.Pending;

	const quickSetupPaths = learningPaths.filter(
		(lp) => lp.for_quick_setup !== false
	);

	const sections = quickSetupPaths.map((lp) => ({
		title: lp.name,
		duration: '',
		description: lp.description,
		courses: [...lp.activities]
			.sort((a, b) => a.order - b.order)
			.map((c, index) => {
				const course = coursesById[c.activity_id];
				return {
					id: c.activity_id,
					title: course?.name ?? `Course ${c.activity_id}`,
					image:
						course?.image_url ||
						PLACEHOLDER_IMAGES[index % PLACEHOLDER_IMAGES.length],
					isMandatory: c.is_required
				};
			})
	}));

	return (
		<div id="lms-ca-learning-path" className={styles.learningPath}>
			<div
				id="lms-ca-learning-path-heading"
				className={styles.learningPath__heading}
			>
				Learning Path Review
			</div>
			<div className={styles.learningPath__desc__container}>
				<div className={styles.learningPath__desc}>
					Quick setup automatically assigns learning paths based on
					employee roles.
				</div>
				<div className={styles.learningPath__desc}>
					Venza offers comprehensive awareness training through three
					curated learning paths: Basic, Intermediate, and Advanced.
					Each curriculum consists of a series of individual courses
					that are carefully designed to provide a standard-setting
					training experience.
				</div>
				<div className={styles.learningPath__desc}>
					Staff receive the Basic Learning Path, Managers receive the
					Basic and Intermediate Paths, and Leadership + IT receive
					all three paths.
				</div>
			</div>

			<div
				id="lms-ca-learning-path-sections"
				className={styles.learningPath__wrapper}
			>
				{isLoading ? (
					<div className={styles.learningPath__loading}>
						<Spin />
					</div>
				) : (
					sections.map((section) => (
						<LearningSection
							key={section.title}
							id={`lms-ca-learning-section-${slugify(section.title)}`}
							{...section}
						/>
					))
				)}
			</div>
		</div>
	);
});

export default LearningPath;
